import { type CollectibleAuctionOneRefined, useIntl } from '@capsicum/common';
import { Card, Label, Timer } from '@capsicum/common-web';
import { useAuctionCard } from '@capsicum/features/fanshop/shared';
import { type ForwardedRef, forwardRef, memo, useMemo } from 'react';
import styled from 'styled-components';

import { AuctionTags } from '../auction-tags/AuctionTags';

export const ITEM_WIDTH = 173;
export const ITEM_HEIGHT = 330;

export type AuctionCardProps = {
	auction: CollectibleAuctionOneRefined;
	onClick?: () => void;
	testID: string;
};

/**
 * AuctionCard is specific to each individual auction in the auctions page
 *
 * @param {AuctionCardProps} props - The props of the container
 * @param {Collectible} props.auction All data relevant to an auction card
 * @param {string} props.testID The ID to enable e2e framework to grab this element
 * @param {() => void} props.onClick The callback function to be called on container click
 * @returns {JSX.Element} The AuctionCard
 */

const AuctionCardComponent = (
	{ auction, onClick, testID }: AuctionCardProps,
	ref?: ForwardedRef<HTMLDivElement> | null
) => {
	const {
		auctionEndDate,
		bidStatusLabel,
		currentStatus,
		getAuctionImage,
		getAuctionStatus,
		hasBids,
		isBall,
		price,
		priceChz,
		setCurrentStatus,
		sortedTags,
		texts
	} = useAuctionCard(auction);

	const { $t } = useIntl();

	const tags = useMemo(
		() => (
			<S.TagWrapper>
				<AuctionTags auctionData={auction} isBall={isBall} sortedTags={sortedTags} />
			</S.TagWrapper>
		),
		[auction, isBall, sortedTags]
	);

	const image = useMemo(
		() =>
			getAuctionImage ? (
				<S.StyledCardImage alt="auction image" optimized src={getAuctionImage} />
			) : (
				<S.StyledCardImagePlaceholder />
			),
		[getAuctionImage]
	);

	const descriptionWrapper = useMemo(
		() => (
			<S.DescriptionWrapper>
				{isBall && <S.GoalBy>{$t('rewards.auctions.goalBy')}</S.GoalBy>}
				<S.Title $hasBids={hasBids} $isGoalBall={isBall}>
					{auction.collectible?.name}
				</S.Title>
				<S.StyledCountDown>
					<Timer
						customFormat="auction"
						customStatus="closed"
						expirationDate={auctionEndDate}
						size="condensed"
						type="default"
						onElapsed={() => setCurrentStatus('closed')}
					/>
				</S.StyledCountDown>
				{hasBids ? (
					<S.InfoWrapper>
						<div>
							<S.Price>{price}</S.Price>
							<S.PriceChz>{`(${priceChz})`}</S.PriceChz>
						</div>
						<S.BidInfo $status={getAuctionStatus()}>{bidStatusLabel()}</S.BidInfo>
					</S.InfoWrapper>
				) : (
					<S.NoBids>{texts.noBids}</S.NoBids>
				)}
			</S.DescriptionWrapper>
		),
		[
			isBall,
			hasBids,
			price,
			priceChz,
			getAuctionStatus,
			bidStatusLabel,
			texts.noBids,
			auction.collectible?.name,
			currentStatus,
			auctionEndDate,
			setCurrentStatus
		]
	);

	return (
		<div data-testid={testID} ref={ref}>
			<S.StyledCard onActivate={onClick}>
				<S.ImageWrapper>
					{image}
					{tags}
				</S.ImageWrapper>
				{descriptionWrapper}
			</S.StyledCard>
		</div>
	);
};

const ForwardedAuctionCardComponent = forwardRef(AuctionCardComponent);

ForwardedAuctionCardComponent.displayName = 'ForwardedAuctionCard';

export const AuctionCard = memo(ForwardedAuctionCardComponent);

namespace S {
	export const StyledCard = styled(Card)`
		width: ${ITEM_WIDTH}px;
		height: ${ITEM_HEIGHT}px;
	`;

	export const ImageWrapper = styled.div`
		position: relative;
	`;

	export const StyledCardImage = styled(Card.Image)`
		height: 160px;
	`;

	export const StyledCardImagePlaceholder = styled.div`
		height: 160px;
		background-color: ${({ theme }) => theme.colors.skeleton.default};
	`;

	export const StyledCountDown = styled.div`
		display: flex;
		flex-direction: row;
		align-items: center;
		padding: ${({ theme }) => `${theme.spacings.xxsmall} 0`};
	`;

	export const TagWrapper = styled.div`
		display: flex;
		flex-direction: column;
		position: absolute;
		bottom: 0;
		left: 0;
		z-index: 2;
	`;

	export const Tag = styled(Label)`
		border-top-left-radius: 0;
		border-bottom-left-radius: 0;
	`;

	export const DescriptionWrapper = styled.div`
		display: flex;
		flex-direction: column;
		padding: ${({ theme }) => theme.spacings.small};
		height: 170px;
	`;

	export const Title = styled(Card.Title)<{ $hasBids: boolean; $isGoalBall: boolean }>`
		${({ theme }) => theme.fonts.headings.title6};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		white-space: nowrap;
		margin-bottom: ${({ $hasBids }) => ($hasBids ? 'auto' : 'unset')};
		margin-top: ${({ $isGoalBall, theme }) => ($isGoalBall ? 0 : theme.spacings.xsmall)};
		text-overflow: ellipsis;
	`;

	export const GoalBy = styled.div`
		color: ${({ theme }) => theme.colors.text.main.unselected};
		${({ theme }) => theme.fonts.text.caption1};
		margin-bottom: 0;
	`;

	export const InfoWrapper = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const Price = styled.div`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.text.text1};
	`;

	export const PriceChz = styled.div`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.text.caption2};
	`;

	export const BidInfo = styled.div<{ $status: string }>`
		${({ theme }) => theme.fonts.text.text4};
		color: ${({ $status, theme }) => {
			switch ($status) {
				case 'winning':
					return theme.colors.text.main.unselected;
				case 'own':
				case 'won':
					return theme.colors.text.brand.enabled;
				default:
					return theme.colors.text.main.enabled;
			}
		}};
	`;

	export const NoBids = styled.div`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		margin-top: ${({ theme }) => theme.spacings.xxsmall};
		${({ theme }) => theme.fonts.text.text4};
	`;
}
